/**
 * El documento — issue #47 (B6.11).
 *
 * Un reporte formal no es la analítica interactiva con otro color. La
 * diferencia es que **se archiva**: alguien lo va a leer dentro de tres años,
 * fuera de la aplicación, y tiene que poder decir de qué período habla, quién
 * lo pidió y cuándo. Por eso la cabecera lleva marca, período, firma
 * (nombre, correo y rol de quien lo generó), instante de generación y una
 * referencia — y por eso el período se guarda con fechas exactas y no como
 * "últimos 30 días", que mañana significa otra cosa.
 *
 * El PDF sale del navegador: `window.print()` sobre un bloque de impresión
 * que deja fuera el resto del dashboard. Un generador de PDF del lado del
 * servidor es lo correcto para el reporte **programado** (que necesita
 * mandarlo por correo sin nadie delante); para el que alguien está mirando,
 * imprimir es el mismo documento, con las fuentes y el idioma que ya están en
 * pantalla, y sin una dependencia nueva en el bundle.
 *
 * Privacidad: todo lo que entra acá son **agregados** — `analytics/overview`
 * y `analytics/timeseries` salen de los rollups. No hay ningún evento
 * individual, ninguna ubicación fina y ningún dato de consumidor, que es la
 * regla 5 del dominio en el borde por donde el dato sale impreso.
 */
import type { ReactNode } from 'react';

import { useApiQuery } from '@/api/hooks';
import { reportKeys, type ReportPeriod } from '@/api/keys';
import type { components } from '@/api/schema';
import type { Role } from '@/api/types';
import { ROLE_LABELS } from '@/auth/roles';
import type { MessageKey } from '@/i18n/messages';
import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { Card, CardBody } from '@/ui/Card';
import { ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';

type Overview = components['schemas']['OverviewOut'];
type Timeseries = components['schemas']['TimeseriesOut'];

/** Quién generó el documento. Es la parte que lo vuelve un reporte formal. */
export interface ReportSignature {
  brand: string;
  tenantSlug: string;
  name: string;
  email: string;
  role: Role;
  /** ISO-8601. El instante en que se pidió, no el de cada render. */
  generatedAt: string;
  reference: string;
}

/** Etiquetas de `RESULT_LABELS` (services/api/analytics/queries.py). */
const RESULT_KEYS: Record<string, MessageKey> = {
  genuine: 'reports.result.genuine',
  already_seen: 'reports.result.already_seen',
  not_activated: 'reports.result.not_activated',
  invalid: 'reports.result.invalid',
  stolen: 'reports.result.stolen',
  recalled: 'reports.result.recalled',
  out_of_territory: 'reports.result.out_of_territory',
};

/**
 * Lo que el equipo de brand protection llama sospechoso. `already_seen` no
 * entra: un consumidor que escanea dos veces su propia caja es lo más común
 * del mundo. Es la misma lista que `SUSPICIOUS_RESULTS` del backend.
 */
const SUSPICIOUS_RESULTS = new Set(['not_activated', 'invalid', 'stolen', 'recalled', 'out_of_territory']);

export function ReportDocument({
  period,
  signature,
}: {
  period: ReportPeriod;
  signature: ReportSignature;
}): ReactNode {
  const { t, fmt } = useI18n();
  const query = { since: period.since, until: period.until };

  const overview = useApiQuery<Overview>(
    reportKeys.overview(period),
    '/api/v1/analytics/overview',
    query,
    { staleTime: 5 * 60_000 },
  );
  const timeseries = useApiQuery<Timeseries>(
    reportKeys.timeseries(period),
    '/api/v1/analytics/timeseries',
    query,
    { staleTime: 5 * 60_000 },
  );

  if (overview.isPending || timeseries.isPending) return <LoadingState rows={6} />;
  if (overview.isError) {
    return (
      <ErrorState
        error={overview.error}
        onRetry={() => {
          void overview.refetch();
        }}
      />
    );
  }
  if (timeseries.isError) {
    return (
      <ErrorState
        error={timeseries.error}
        onRetry={() => {
          void timeseries.refetch();
        }}
      />
    );
  }

  const data = overview.data;
  const days = byDay(timeseries.data.points);

  return (
    <>
      {/*
        Sólo el documento se imprime. Sin esto, el PDF sale con la barra
        lateral, la cabecera y los botones — que es lo que hace que un
        "exportar a PDF" hecho con el navegador se vea improvisado.
      */}
      <style>{PRINT_CSS}</style>

      <div className="mb-3 flex justify-end print:hidden">
        <Button
          variant="primary"
          onClick={() => {
            window.print();
          }}
        >
          {t('reports.print')}
        </Button>
      </div>

      <Card id="xyv-report" className="print:border-0">
        <CardBody className="space-y-6">
          <header className="space-y-3 border-b border-border pb-4">
            <h2 className="text-lg font-semibold text-ink">{t('reports.doc.title')}</h2>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <Meta label={t('reports.doc.brand')} value={`${signature.brand} (${signature.tenantSlug})`} />
              <Meta
                label={t('reports.doc.period')}
                value={`${fmt.date(`${period.since}T00:00:00Z`)} — ${fmt.date(`${period.until}T00:00:00Z`)}`}
              />
              <Meta
                label={t('reports.doc.generatedBy')}
                value={`${signature.name} · ${signature.email} · ${ROLE_LABELS[signature.role]}`}
              />
              <Meta label={t('reports.doc.generatedAt')} value={fmt.dateTime(signature.generatedAt)} />
              <Meta label={t('reports.doc.reference')} value={signature.reference} />
            </dl>
          </header>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-ink">{t('reports.doc.kpis')}</h3>
            <dl className="grid gap-4 sm:grid-cols-3">
              <Meta label={t('reports.doc.totalScans')} value={fmt.number(data.total_scans)} />
              <Meta label={t('reports.doc.genuine')} value={fmt.number(data.genuine_scans)} />
              <Meta label={t('reports.doc.suspicious')} value={fmt.number(data.suspicious_scans)} />
              <Meta
                label={t('reports.doc.suspiciousRate')}
                value={fmt.percent(data.suspicious_rate)}
              />
              <Meta label={t('reports.doc.openAlerts')} value={fmt.number(data.open_alerts)} />
              <Meta
                label={t('reports.doc.criticalAlerts')}
                value={fmt.number(data.critical_open_alerts)}
              />
            </dl>
          </section>

          {data.total_scans === 0 ? (
            <p className="text-sm text-ink-muted">{t('reports.doc.empty')}</p>
          ) : (
            <>
              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-ink">{t('reports.doc.byResult')}</h3>
                <Table caption={t('reports.doc.byResult')}>
                  <thead>
                    <tr>
                      <Th>{t('reports.doc.result')}</Th>
                      <Th className="text-right">{t('reports.doc.scans')}</Th>
                      <Th className="text-right">{t('reports.doc.share')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_result.map((row) => (
                      <tr key={row.result}>
                        <Td>{resultLabel(row.label, t)}</Td>
                        <Td className="text-right">{fmt.number(row.scans)}</Td>
                        <Td className="text-right">
                          {fmt.percent(data.total_scans === 0 ? 0 : row.scans / data.total_scans)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </section>

              {data.top_countries.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold text-ink">{t('reports.doc.countries')}</h3>
                  <Table caption={t('reports.doc.countries')}>
                    <thead>
                      <tr>
                        <Th>{t('reports.doc.country')}</Th>
                        <Th className="text-right">{t('reports.doc.scans')}</Th>
                        <Th className="text-right">{t('reports.doc.suspicious')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_countries.map((row) => (
                        <tr key={row.country_code}>
                          <Td className="font-mono">{row.country_code}</Td>
                          <Td className="text-right">{fmt.number(row.scans)}</Td>
                          <Td className="text-right">{fmt.number(row.suspicious)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </section>
              )}

              {days.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold text-ink">{t('reports.doc.daily')}</h3>
                  <Table caption={t('reports.doc.daily')}>
                    <thead>
                      <tr>
                        <Th>{t('reports.doc.day')}</Th>
                        <Th className="text-right">{t('reports.doc.scans')}</Th>
                        <Th className="text-right">{t('reports.doc.suspicious')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {days.map((row) => (
                        <tr key={row.day}>
                          <Td>{fmt.date(`${row.day}T00:00:00Z`)}</Td>
                          <Td className="text-right">{fmt.number(row.scans)}</Td>
                          <Td className="text-right">{fmt.number(row.suspicious)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </section>
              )}
            </>
          )}

          <footer className="space-y-1 border-t border-border pt-4 text-xs text-ink-subtle">
            {data.data_refreshed_at !== null && data.data_refreshed_at !== undefined && (
              <p>{t('reports.doc.refreshed', { when: fmt.dateTime(data.data_refreshed_at) })}</p>
            )}
            <p>{t('reports.doc.privacy')}</p>
            <p>{t('reports.doc.footer')}</p>
          </footer>
        </CardBody>
      </Card>
    </>
  );
}

function Meta({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-ink-subtle uppercase">{label}</dt>
      <dd className="text-sm text-ink">{value}</dd>
    </div>
  );
}

function resultLabel(label: string, t: (key: MessageKey) => string): string {
  const key = RESULT_KEYS[label];
  return key === undefined ? label : t(key);
}

interface DayRow {
  day: string;
  scans: number;
  suspicious: number;
}

/**
 * La serie viene larga (una fila por día **y** resultado) porque pivotearla
 * en el servidor ataría la respuesta a la lista de resultados. Acá se suma
 * por día.
 *
 * `units` no se agrega: es "unidades distintas por día y resultado", y
 * sumarlo entre resultados contaría dos veces la unidad que aparece en dos.
 * Un reporte formal no puede llevar un número que no cierra.
 */
function byDay(points: Timeseries['points']): DayRow[] {
  const rows = new Map<string, DayRow>();
  for (const point of points) {
    const row = rows.get(point.day) ?? { day: point.day, scans: 0, suspicious: 0 };
    row.scans += point.scans;
    if (SUSPICIOUS_RESULTS.has(point.label)) row.suspicious += point.scans;
    rows.set(point.day, row);
  }
  return [...rows.values()].sort((a, b) => a.day.localeCompare(b.day));
}

/**
 * Aísla el documento al imprimir. Va acá y no en `styles/global.css` porque
 * es de este módulo: sólo aplica cuando el documento está montado.
 */
const PRINT_CSS = `
@media print {
  body * { visibility: hidden; }
  #xyv-report, #xyv-report * { visibility: visible; }
  #xyv-report { position: absolute; inset: 0; width: 100%; }
}
`;
